<template>
  <div class="persons dx-viewport" v-if="canEdit || canDelete" v-cloak>
    <GridPanelHeader
      :title="$t('works.persons.name')"
      :grid-ref="$refs.personsGrid"
      :show-add="true"
      :show-filter="false"
      @add="onAddPerson"
    />
    <DxDataGrid
      ref="personsGrid"
      :data-source="dataSource"
      :remote-operations="gridRemoteOperations"
      :show-column-lines="true"
      :show-row-lines="false"
      :column-auto-width="true"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :row-alternation-enabled="true"
      :show-borders="true"
      @content-ready="onContentReady"
      :element-attr="gridAttributes"
      :column-hiding-enabled="true"
    >
      <DxColumn data-field="name" data-type="string" :caption="$t('common.name')" />
      <DxColumn
        data-field="department_uuid"
        cell-template="departmentViewTemplate"
        :caption="$t('common.unit')"
        :min-width="160"
        :allow-filtering="true"
        :filter-operations="['=']"
        :calculateDisplayValue="getDepartmentColumnText"
      >
        <DxLookup :data-source="departmentList" value-expr="uuid" display-expr="name" />
      </DxColumn>
      <template #departmentViewTemplate="{ data }">
        <span>
          {{ getDepartmentColumnText(data.data.department_uuid) }}
        </span>
      </template>
      <DxColumn
        data-field="created_at"
        data-type="date"
        :formItem="{
          visible: false,
        }"
        :format="getDxGridDefaultDateFormat"
        :caption="$t('common.created_at')"
      />
      <DxColumn
        data-field="updated_at"
        data-type="date"
        :formItem="{
          visible: false,
        }"
        :format="getDxGridDefaultDateFormat"
        :caption="$t('common.last_update')"
      />

      <DxColumn
        :width="60"
        alignment="center"
        :fixed="true"
        fixed-position="right"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        :formItem="{
          visible: false,
        }"
        cell-template="dynamicMenuCell"
      />
      <template #dynamicMenuCell="{ data }">
        <DxActionMenu
          :grid-ref="$refs.personsGrid"
          row-key="uuid"
          :row="data.data"
          :can-edit="canEdit"
          :can-delete="canDelete"
          :allow-deleting="allowDeleting"
        />
      </template>

      <DxHeaderFilter :visible="false" />
      <DxGroupPanel :visible="false" />
      <DxGrouping :auto-expand-all="false" />
      <DxPaging :total="3500" :page-size="limit" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
        :info-text="$t('common.page_info')"
      />
      <DxFilterRow :visible="true" />
      <DxColumnChooser :enabled="false" mode="select" />
      <DxEditing :allow-adding="false" :allow-updating="false" :allow-deleting="false" :confirm-delete="true" mode="form" :use-icons="true">
        <DxTexts
          :confirm-delete-message="
            $t('common.confirm_delete', {
              title: $t('works.persons.name'),
            })
          "
          :confirm-delete-title="$t('common.confirm_delete_title')"
          :deleteRow="$t('common.delete')"
        />
      </DxEditing>
    </DxDataGrid>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DxButton from "devextreme-vue/button";
import {
  DxDataGrid,
  DxColumn,
  DxGrouping,
  DxGroupPanel,
  DxPager,
  DxPaging,
  DxHeaderFilter,
  DxFilterRow,
  DxToolbar,
  DxItem,
  DxSorting,
  DxEditing,
  DxTexts,
  DxLookup,
  DxColumnChooser,
} from "devextreme-vue/data-grid";
import Forbidden from "~/pages/errors/Forbidden.vue";
import { canDelete, canEdit, permissions, permissionTypes } from "~/helpers/Permissions";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";
import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { format } from "date-fns";

export default {
  name: "persons",
  data() {
    return {
      limit: 15,
      loading: false,
      gridRemoteOperations: {
        filtering: false,
        sorting: false,
      },
      pageSizes: [15, 50, 100],
      gridAttributes: {
        class: "smagria-grid",
      },
    };
  },
  computed: {
    ...mapGetters({
      departmentList: "department/departmentList",
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
    dataSource() {
      return new DataSource({
        store: new CustomStore({
          key: "uuid",
          load: this.load,
          insert: this.create,
          update: this.update,
          remove: this.remove,
        }),
      });
    },
    config() {
      return {
        permission: permissions.PERSONS,
        permission_type: permissionTypes.access,
      };
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
  },
  components: {
    Forbidden,
    DxButton,
    DxDataGrid,
    DxColumn,
    DxGrouping,
    DxGroupPanel,
    DxPager,
    DxPaging,
    DxHeaderFilter,
    DxFilterRow,
    DxToolbar,
    DxItem,
    DxSorting,
    DxEditing,
    DxTexts,
    DxLookup,
    DxColumnChooser,
    GridPanelHeader,
    DxActionMenu,
  },
  methods: {
    ...mapActions({
      getPersons: "works-persons/getPersons",
      createPerson: "works-persons/createPerson",
      updatePerson: "works-persons/updatePerson",
      deletePerson: "works-persons/deletePerson",
      listDepartments: "department/listDepartments",
    }),
    onContentReady() {},
    async load(data) {
      let dataSource = {
        data: [],
        totalCount: 0,
      };
      await this.getPersons({
        filter: data.filter,
        sort: data.sort,
        limit: 9999,
        enablePagination: 0,
      }).then((resp) => {
        dataSource.data = resp.data;
        dataSource.totalCount = resp.data.length;
      });
      return dataSource;
    },
    async create(obj) {
      await this.createPerson(obj);
    },
    async update(key, obj) {
      const updatable = Object.assign(
        {},
        this.dataSource.items().find((item) => item.uuid === key),
        obj,
      );
      await this.updatePerson(updatable);
    },
    async remove(uuid) {
      await this.deletePerson({ uuid });
    },
    allowDeleting(e) {
      const rowData = e && e.row ? e.row.data : e;
      return this.canDelete && (rowData?.is_deletable === 1 || rowData?.is_deletable === undefined);
    },
    getDepartmentColumnText(department_uuid) {
      const department = this.departmentList.find((d) => d.uuid == department_uuid);
      return department ? department.name : "";
    },
    onAddPerson() {
      this.$refs.personsGrid.instance.addRow();
    },
    onExporting(e) {
      e.fileName = "Persons_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss");
      e.cancel = true;

      if (e.format === "xlsx") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Persons");

        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
          customizeCell: ({ gridCell, excelCell }) => {
            if (gridCell?.rowType === "data" && gridCell?.column?.dataField === "department_uuid") {
              excelCell.value = gridCell.displayValue || this.getDepartmentColumnText(gridCell.value);
            }
          },
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), e.fileName + ".xlsx");
          });
        });
      }
    },
  },
  mounted() {
    this.listDepartments({ enablePagination: 0, limit: 9999 });
  },
};
</script>

<style scoped></style>
